import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Button, Form, Stack } from 'react-bootstrap';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonSEOTitle from '../../../../helpers/CommonSEOTitle';
import AccountNavigation from '../Navigation/AccountNavigation';
import { TNTable, TNButton } from '../../../../common/components';
import { 
  useGetProfile, 
  useGetUserSkillsScore,
  useGetSkillParameterDropdown,

} from '@hooks';
import { getStoredDataUsingKey } from '../../../../helpers/localStorageHelper';
import { setPageMoveToFirstPage } from '../../../../helpers';
import SearchIcon from '../../../../assets/images/svg/search.svg';
import CloseIcon from '../../../../assets/images/svg/close.svg';
import AddIcon from '../../../../assets/images/svg/add.svg';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import validationSchema from '../../UsersMaster/Users/UserSkillsScore/AddEditUserSkillsScoreValidation';
import { UserSkillsScoreService } from '@api/UserSkillsScoreService';
import viewProgressIcon from '../../../../assets/images/svg/view-progress.svg';

const MyUserSkillsScore = ({ t }) => {
  const storageKey = 'my-user-skills-score';
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(
    getStoredDataUsingKey(storageKey, 'currentPage', 1)
  );
  const [data, setData] = useState([]);
  const [paginationData, setPaginationData] = useState(null);
  const [profileData, setProfileData] = useState(null);

  const [searchValue, setSearchValue] = useState('');
  const searchDebounceTimeout = useRef(null);
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [skills, setSkills] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [scoreOptions, setScoreOptions] = useState([]);

  // 🔹 Fetch profile details
  const { isLoading: isLoadingProfile } = useGetProfile(
    ({ data: profile }) => setProfileData(profile),
    (error) => {
      toast.error(error.message);
      if (error?.data?.redirect) navigate('/dashboard');
    }
  );

  // 🔹 Restrict access to only normal users (user_type === 2)
  useEffect(() => {
    if (profileData?.user_type && profileData.user_type !== 2) {
      navigate('/my-profile');
    }
  }, [profileData]);

  // 🔹 Fetch user's skill scores
  const { refetch } = useGetUserSkillsScore(
    [currentPage, searchValue],
    (res) => {
      const list = res?.data?.user_skills_and_score_list || [];
      const pagination = res?.data?.pagination || null;

      setData(list);
      setPaginationData(pagination);

      if (list.length === 0 && currentPage > 1) {
        setPageMoveToFirstPage(setCurrentPage, storageKey);
      }
    },
    (error) => {
      toast.error(error.message);
      if (error?.data?.redirect) navigate('/dashboard');
    }
  );

  // 🔹 Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (searchDebounceTimeout.current) clearTimeout(searchDebounceTimeout.current);
    searchDebounceTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      refetch();
    }, 500);
  };

  // 🔹 Clear filter
  const handleClearFilter = () => {
    setSearchValue('');
    setCurrentPage(1);
    refetch();
  };

  // 🔹 Handle pagination
  const pageIndexHandle = (pageIndex) => {
    setCurrentPage(pageIndex + 1);
  };

    // Formik
    const formik = useFormik({
      initialValues: { scores_note: '', skill_scoring_parameters_id: '', skills_scoring_option_id: '' },
      enableReinitialize: true,
      validationSchema,
      onSubmit: (values) => {
        const payload = { ...values, user_id: userId };
  
        if (isEditMode) {
          doUpdate({ id: editId, ...payload }); // PUT
        } else {
          doAdd(payload); // POST
        }
      }
    });
  // On Load Fetch Records
  const { refetch: getSkillParameterDropdown } = useGetSkillParameterDropdown(
    (res) => {

      // FIX: access nested 'data' object
      const list = res?.data?.skill_parameters_list || [];
      setSkills(list);

    },
    handleError
  );
  const handleError = (error) => {
    if (errorHandled) return;
    errorHandled = true;

    if (error?.data?.redirect === true) {
      toast.error(error.message);
      navigate('/dashboard');
    } else {
      toast.error(error.message);
    }

    // Reset after short delay (optional, allows next error to show after e.g. 1s)
    setTimeout(() => {
      errorHandled = false;
    }, 1000);
  };
    // handleModal
  const handleModalOpen = (editData = null) => {
    getSkillParameterDropdown();
    if (editData) {
      setIsEditMode(true);
      setEditId(editData.user_skills_scores_id);
      formik.setValues({
        option_name: editData.option_name || '',
        option_note: editData.option_note || '',
        option_status: editData.option_status || '',
        option_color: editData.option_color || ''
      });
    } else {
      setIsEditMode(false);
      setEditId(null);
      formik.resetForm();
    }
    setShowModal(true);
  };
    //view 
  const handleViewClick = (event, skill_scoring_parameters_id) => {
    const url = `/my-profile/skills/${skill_scoring_parameters_id}`;
    if (event.ctrlKey || event.metaKey) {
      // Ctrl + Click (Windows/Linux) or Cmd + Click (Mac) => Open in new tab
      window.open(url, '_blank');
    } else {
      // Normal click => Navigate in same tab
      navigate(url);
    }
  };
  
  const actionColumn = {
      Header: t('page.action_column'),
      accessor: 'skill_scoring_parameters_id',
      Cell: ({ value: scoreId }) => {
        return (
          <div className="d-flex" style={{ gap: '12px' }}>
            {/* {shouldShowRoute('user.skills.and.score.index') && ( */}
              <TNButton
                className="table-button text-btns btn btn-primary"
                id={scoreId}
                onClick={(event) => handleViewClick(event, scoreId)}
                style={{
                  width: 'fit-content',
                  display: 'flex',
                  borderRadius: '100px',
                  fontSize: '11px',
                  gap: '5px'
                }}
              >
                <img src={`admin/${viewProgressIcon}`} alt="view-progress" style={{height: '13px', width: '13px'}} />
                View Progress
              </TNButton>
            {/* )} */}
          </div>
        );
      },
      disableSortBy: true
    };

    // 🔹 Define columns
    const columnsjson = [
      {
        Header: `${t('page.user_skills_score_created_by')}`,
        accessor: 'created_by_user',
        disableSortBy: true,
        Cell: ({ row }) => (
          <div>
            <div>{row.original.updated_at}</div>
            <span className='status-label gray'>{row.original.updated_by_user || '-'}</span>
          </div>
        )
      },
      {
        Header: `${t('page.user_skills_score_name')}`,
        accessor: row => {
          // find the correct skill that matches this record’s skill_scoring_parameters_id
          const skill = Array.isArray(row.skill_scoring_parameter)
            ? row.skill_scoring_parameter.find(
              s => s.skill_scoring_parameters_id === row.skill_scoring_parameters_id
            )
            : null;
          return skill?.skill_name || '-';
        },
        disableSortBy: true
      },
      {
        Header: `${t('page.user_skills_score_comment')}`,
        accessor: 'skills_scoring_option',
        Cell: ({ row }) => {
          const [expanded, setExpanded] = useState(false);
          const toggleExpand = () => setExpanded((prev) => !prev);
          const optionsArray = row.original.skills_scoring_option || [];
          const option = Array.isArray(optionsArray)
            ? optionsArray.find(
              o => o.skills_scoring_options_id === row.original.skills_scoring_option_id
            )
            : null;

          if (!option) return null;

          const scoresNote = row.original.scores_note || '';

          const content = scoresNote;
          const truncated = content.length > 70 ? content.substring(0, 70) + '...' : content;

          return (
            <div>
              <span className={`status-label ${option.option_color == 1 ? 'green' : option.option_color == 2 ? 'blue' : option.option_color == 3 ? 'red' : ''}`}>{option.option_name}</span>
              {scoresNote && (
                <div
                  style={{
                    fontSize: '0.9em',
                    color: '#6c757d',
                    marginTop: '8px',
                    maxWidth: '300px',
                    whiteSpace: 'break-spaces'
                  }}
                >
                  <p
                    dangerouslySetInnerHTML={{
                      __html: expanded ? content : truncated
                    }}
                    className="m-0"
                  />
                  {content.length > 70 && (
                    <span
                      onClick={toggleExpand}
                      className="btn btn-link p-0"
                      style={{ fontSize: '0.85rem' }}
                    >
                      {expanded ? 'Read less' : 'Read more'}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        },
        disableSortBy: true
      }

    ];
    columnsjson.push(actionColumn);
  const columns = useMemo(() => columnsjson, []);

  if (!paginationData) return null;

  return (
    <>
      {profileData && Object.keys(profileData).length > 0 ? (
        <>
          <CommonSEOTitle title={t('page.user_skills_score')} />
          <AccountNavigation
            t={t}
            formatted_joining_date={profileData?.formatted_joining_date}
            formatted_last_working_date={profileData?.formatted_last_working_date}
            data={profileData}
          />
          {/* 🔹 Data Table */}
          <div style={{ clear: 'both', marginTop: '20px' }}>
            <TNTable
              columns={columns}
              data={data}
              paginationData={paginationData}
              onSelectPage={pageIndexHandle}
              t={t}
              pageIndexGet={currentPage - 1}
              key={Math.floor(Math.random() * 1000)}
            />
          </div>
        </>
       ) : null}
    </>
  );
};

export default MyUserSkillsScore;
